/**
 * Capture Timing Logger
 *
 * Lightweight, focused timing instrumentation for the container / waste-label
 * image capture flow. Built on top of the existing CloudWatch (KatalLogger)
 * pipeline so it is automatically enabled on deployed stacks (beta / personal)
 * where `NODE_ENV=production` and a `LoggerUrl` is injected at build time.
 *
 * What it measures (from the user / browser perspective):
 *  - totalMs: user-perceived total time to successfully capture one
 *    container or waste-label image, from when the camera stream becomes
 *    ready until the capture is accepted (auto) or uploaded (manual).
 *  - network latency (round-trip browser <-> SG WebSocket endpoint), surfaced
 *    as avg / p50 / p90 from the streaming metrics already collected.
 *  - image size (KB) as a proxy for low / high res upload volume.
 *
 * Dimensions:
 *  - captureMode: "auto" (backend-triggered READY_FOR_ANALYZER) vs "manual" (user tapped the capture button).
 *  - objectType: "container" vs "label".
 *
 * ============================================
 * CLOUDWATCH METRICS (namespace: SafetyGenie/ClientCapture)
 * ============================================
 * Published via EMF, dimensions captureMode x objectType. Latency/size values
 * are raw per-capture; prefer CloudWatch statistics (Average / p50 / p90 / p99)
 * on the *LatencyMs metrics for aggregates. The *P50Ms / *P90Ms metrics below
 * are client-computed session percentiles (per-capture snapshots) and are
 * approximate when aggregated.
 *  - TotalCaptureTimeMs                       user-perceived total (always)
 *  - LowResStateTimeMs                        stream-ready → first HIGH_RES (low-res state; only when transition observed)
 *  - HighResStateTimeMs                       first HIGH_RES → accepted (high-res state; only when transition observed)
 *  - ManualCaptureFrameMs                     browser grab+encode (manual only)
 *  - ManualUploadMs                           REST high-res upload round-trip (manual only)
 *  - WsRoundTripLatencyMs                     overall WS round-trip avg (when sampled)
 *  - WsRoundTripLatencyP50Ms / P90Ms          overall WS round-trip percentiles
 *  - LatencySampleCount                       overall WS round-trip sample count
 *  - LowResLatencyMs                          WS round-trip avg, low-res frames
 *  - LowResLatencyP50Ms / P90Ms               WS round-trip percentiles, low-res
 *  - LowResLatencySampleCount                 low-res sample count
 *  - HighResLatencyMs                         WS round-trip avg, high-res frames
 *  - HighResLatencyP50Ms / P90Ms              WS round-trip percentiles, high-res
 *  - HighResLatencySampleCount                high-res sample count
 *  - AvgImageSizeKB / LastImageSizeKB         overall per-frame payload size
 *  - LowResImageSizeKB / HighResImageSizeKB   per-frame payload size by resolution
 *
 * ============================================
 * QUERY IN CLOUDWATCH LOGS INSIGHTS (per-run detail):
 * ============================================
 *   fields katalLogger.message
 *   | filter katalLogger.message like /\[CaptureTiming\]/
 *   | sort @timestamp desc
 *
 * Each log line carries `inspectionId` (log only, not a metric dimension) so a
 * single run can be traced end-to-end. To scope to one inspection:
 *   | filter katalLogger.message like /"inspectionId":"<id>"/
 */

import { cloudWatchLogger } from "./CloudWatchLoggerService";

export type CaptureMode = 'auto' | 'manual';
export type ObjectType = 'container' | 'label' | 'unknown';

export interface CaptureNetworkMetrics {
  /** Average round-trip WS latency over the session (ms) */
  avgLatencyMs?: number | null;
  /** Median (p50) round-trip WS latency (ms) */
  p50LatencyMs?: number | null;
  /** p90 round-trip WS latency (ms) */
  p90LatencyMs?: number | null;
  /** Number of latency samples (round-trips) the percentiles were computed from */
  latencySampleCount?: number;
  /** Average per-frame payload size over the session (KB) */
  avgImageSizeKB?: number;
  /** Most recent frame payload size (KB) */
  lastImageSizeKB?: number;

  // --- Low-res streaming frames ---
  lowResAvgLatencyMs?: number | null;
  lowResP50LatencyMs?: number | null;
  lowResP90LatencyMs?: number | null;
  lowResLatencySampleCount?: number;
  lowResAvgImageSizeKB?: number | null;

  // --- High-res capture frames ---
  highResAvgLatencyMs?: number | null;
  highResP50LatencyMs?: number | null;
  highResP90LatencyMs?: number | null;
  highResLatencySampleCount?: number;
  highResAvgImageSizeKB?: number | null;
}

export interface CaptureSuccessParams extends CaptureNetworkMetrics {
  captureMode: CaptureMode;
  objectType: ObjectType;
  /**
   * Site code the inspection is running at (e.g. "BFI4"). Log only, not a
   * metric dimension.
   */
  siteCode?: string | null;
  /**
   * SafetyGenie inspection id this capture belongs to (log only, not a metric
   * dimension — unbounded cardinality). Lets a run be correlated end-to-end
   * with the barcode-scan logs and backend/service logs for the same inspection.
   */
  inspectionId?: string | null;
  /** Scanned barcode / object id of the container being inspected, if any */
  containerId?: string | null;
  /** 1-based index of the container in the current inspection run */
  containerIndex?: number | null;
  /** Total number of containers in the current inspection run */
  totalContainers?: number | null;
  // --- Manual-capture component times (REST path; null for auto) ---
  /** Browser time to grab + encode the high-res frame (ms) */
  manualCaptureFrameMs?: number | null;
  /** REST round-trip to upload the high-res image and get the response (ms) */
  manualUploadMs?: number | null;
}

interface CaptureMark {
  startTime: number;
  objectType: ObjectType;
  /**
   * Timestamp of the first low-res → high-res transition (backend sent
   * image_next_stage === "HIGH_RES", via high-criteria pass or force_send).
   * null until/unless the transition happens. Used to split totalMs into the
   * low-res positioning phase and the high-res confirm phase.
   */
  highResTime: number | null;
}

class CaptureTimingLogger {
  private currentMark: CaptureMark | null = null;

  /**
   * Resolve container vs label from the workflow target classes.
   * Container targets are "Drum"; label targets are "Waste Label" /
   * "Printed Waste Label".
   */
  resolveObjectType(targetClasses?: string[]): ObjectType {
    if (!targetClasses || targetClasses.length === 0) {
      return "unknown";
    }
    const joined = targetClasses.join(" ").toLowerCase();
    if (joined.includes("label")) {
      return "label";
    }
    if (joined.includes("drum") || joined.includes("container")) {
      return "container";
    }
    return "unknown";
  }

  /**
   * Mark the start of a capture attempt. Called when the camera stream
   * becomes ready for a given detect-object step.
   */
  start(objectType: ObjectType): void {
    this.currentMark = { startTime: Date.now(), objectType, highResTime: null };
  }

  /** Whether a capture is currently being timed. */
  isActive(): boolean {
    return this.currentMark !== null;
  }

  /**
   * Mark the moment the capture switches from low-res streaming to high-res
   * (backend sends image_next_stage === "HIGH_RES", triggered by a
   * high-criteria pass or a force_send). This is the boundary between the
   * low-res "moving into a good position" phase and the high-res "confirm"
   * phase.
   *
   * Only the FIRST transition is recorded — if the stream bounces high → low →
   * high again, lowResStateMs still reflects the time to first reach a good
   * position. No-op if there is no active mark or the transition was already
   * recorded.
   *
   * Note: we deliberately time the state TRANSITION here rather than infer it
   * from per-resolution frame/latency sample counts. The high-res window is
   * often just a frame or two (READY_FOR_ANALYZER can arrive before the first
   * high-res frame is even sampled), so sample counts are not a reliable proxy
   * for whether/when high-res was entered.
   */
  markHighResTransition(): void {
    if (this.currentMark && this.currentMark.highResTime === null) {
      this.currentMark.highResTime = Date.now();
    }
  }

  /**
   * Record a successful capture and emit a timing log line. The total time is
   * computed from the last `start()` mark. No-op if there is no active mark.
   */
  recordSuccess(params: CaptureSuccessParams): void {
    const mark = this.currentMark;
    if (!mark) {
      return;
    }
    const successTime = Date.now();
    const totalMs = successTime - mark.startTime;

    // Split the total into the low-res positioning phase and the high-res
    // confirm phase, using the actual state-transition timestamp captured by
    // markHighResTransition() — NOT frame/latency sample counts.
    //  - lowResStateMs: stream-ready → first HIGH_RES. How long the inspector
    //    spent moving/aiming to get into a good position.
    //  - highResStateMs: first HIGH_RES → capture accepted. High-res confirm /
    //    upload time. null when the transition was never observed (e.g. a
    //    manual capture where the user tapped the shutter before high-res).
    const lowResStateMs = (mark.highResTime ?? successTime) - mark.startTime;
    const highResStateMs = mark.highResTime != null ? successTime - mark.highResTime : null;

    this.currentMark = null;

    const payload = {
      metric: "CaptureTiming",
      captureMode: params.captureMode,
      objectType: params.objectType,
      siteCode: params.siteCode ?? null,
      inspectionId: params.inspectionId ?? null,
      containerId: params.containerId ?? null,
      containerIndex: params.containerIndex ?? null,
      totalContainers: params.totalContainers ?? null,
      totalMs,
      lowResStateMs,
      highResStateMs,
      manualCaptureFrameMs: params.manualCaptureFrameMs ?? null,
      manualUploadMs: params.manualUploadMs ?? null,
      avgLatencyMs: params.avgLatencyMs ?? null,
      p50LatencyMs: params.p50LatencyMs ?? null,
      p90LatencyMs: params.p90LatencyMs ?? null,
      latencySampleCount: params.latencySampleCount ?? 0,
      avgImageSizeKB: params.avgImageSizeKB ?? null,
      lastImageSizeKB: params.lastImageSizeKB ?? null,
      lowResAvgLatencyMs: params.lowResAvgLatencyMs ?? null,
      lowResP50LatencyMs: params.lowResP50LatencyMs ?? null,
      lowResP90LatencyMs: params.lowResP90LatencyMs ?? null,
      lowResLatencySampleCount: params.lowResLatencySampleCount ?? 0,
      lowResAvgImageSizeKB: params.lowResAvgImageSizeKB ?? null,
      highResAvgLatencyMs: params.highResAvgLatencyMs ?? null,
      highResP50LatencyMs: params.highResP50LatencyMs ?? null,
      highResP90LatencyMs: params.highResP90LatencyMs ?? null,
      highResLatencySampleCount: params.highResLatencySampleCount ?? 0,
      highResAvgImageSizeKB: params.highResAvgImageSizeKB ?? null,
    };

    // Per-run, greppable log line. cloudWatchLogger forwards INFO logs to
    // CloudWatch on deployed stacks and to the console locally. This keeps the
    // full detail (percentiles + sample counts) for individual-run debugging.
    cloudWatchLogger.info(`[CaptureTiming] ${JSON.stringify(payload)}`);

    // Emit an EMF document so CloudWatch auto-publishes real metrics for the
    // aggregate view. The raw latency/size values (e.g. WsRoundTripLatencyMs)
    // are per-capture, so the cleanest aggregate is CloudWatch's own
    // avg/p50/p90 statistic applied to those metrics.
    //
    // We ALSO publish the client-computed per-capture percentiles
    // (WsRoundTripLatencyP50Ms, LowResLatencyP50Ms, etc.) as a convenience
    // signal. These are snapshots of each session's distribution, so
    // aggregating them across captures is approximate (a percentile-of-
    // percentiles) — prefer the raw-metric statistic above for precision.
    //
    // Metrics are only included when actually measured (null/no-sample values
    // are skipped) so we don't pollute the aggregate with zeros. All share the
    // captureMode x objectType dimension set; low/high res are distinct metric
    // names rather than an extra dimension to keep cardinality low.
    const metricValues: Record<string, number> = {
      TotalCaptureTimeMs: totalMs,
    };
    // Only publish the low-res / high-res state split metrics when the low→high
    // transition was actually observed. Without it (e.g. a manual capture
    // triggered while still in the low-res state), lowResStateMs equals totalMs
    // and would include manualCaptureFrameMs + manualUploadMs — polluting the
    // low-res "positioning time" aggregate. The raw values are still in the log
    // line for drill-down; we just keep them out of the metric here.
    if (highResStateMs != null) {
      metricValues.LowResStateTimeMs = lowResStateMs;
      metricValues.HighResStateTimeMs = highResStateMs;
    }

    // --- Manual-only component timings (REST high-res path) ---
    if (params.manualCaptureFrameMs != null) {
      metricValues.ManualCaptureFrameMs = params.manualCaptureFrameMs;
    }
    if (params.manualUploadMs != null) {
      metricValues.ManualUploadMs = params.manualUploadMs;
    }

    // --- Overall WS round-trip latency (avg + client-computed percentiles) ---
    // Percentiles are per-capture snapshots of the session-level distribution;
    // aggregating them in CloudWatch is approximate. The raw *LatencyMs metric
    // with CloudWatch's own p50/p90 statistic remains the cleaner aggregate.
    if ((params.latencySampleCount ?? 0) > 0) {
      if (params.avgLatencyMs != null) metricValues.WsRoundTripLatencyMs = params.avgLatencyMs;
      if (params.p50LatencyMs != null) metricValues.WsRoundTripLatencyP50Ms = params.p50LatencyMs;
      if (params.p90LatencyMs != null) metricValues.WsRoundTripLatencyP90Ms = params.p90LatencyMs;
      metricValues.LatencySampleCount = params.latencySampleCount ?? 0;
    }

    // --- Low-res streaming frames (avg + percentiles + sample count) ---
    if ((params.lowResLatencySampleCount ?? 0) > 0) {
      if (params.lowResAvgLatencyMs != null) metricValues.LowResLatencyMs = params.lowResAvgLatencyMs;
      if (params.lowResP50LatencyMs != null) metricValues.LowResLatencyP50Ms = params.lowResP50LatencyMs;
      if (params.lowResP90LatencyMs != null) metricValues.LowResLatencyP90Ms = params.lowResP90LatencyMs;
      metricValues.LowResLatencySampleCount = params.lowResLatencySampleCount ?? 0;
    }

    // --- High-res capture frames (avg + percentiles + sample count) ---
    if ((params.highResLatencySampleCount ?? 0) > 0) {
      if (params.highResAvgLatencyMs != null) metricValues.HighResLatencyMs = params.highResAvgLatencyMs;
      if (params.highResP50LatencyMs != null) metricValues.HighResLatencyP50Ms = params.highResP50LatencyMs;
      if (params.highResP90LatencyMs != null) metricValues.HighResLatencyP90Ms = params.highResP90LatencyMs;
      metricValues.HighResLatencySampleCount = params.highResLatencySampleCount ?? 0;
    }

    // --- Image sizes (KB) ---
    if (params.avgImageSizeKB != null) metricValues.AvgImageSizeKB = params.avgImageSizeKB;
    if (params.lastImageSizeKB != null) metricValues.LastImageSizeKB = params.lastImageSizeKB;
    if (params.lowResAvgImageSizeKB != null) metricValues.LowResImageSizeKB = params.lowResAvgImageSizeKB;
    if (params.highResAvgImageSizeKB != null) metricValues.HighResImageSizeKB = params.highResAvgImageSizeKB;

    const unitFor = (name: string): string => {
      if (name.endsWith("KB")) return "Kilobytes";
      if (name.endsWith("Count")) return "Count";
      return "Milliseconds";
    };
    const metricDefs = Object.keys(metricValues).map((name) => ({
      Name: name,
      Unit: unitFor(name),
    }));

    cloudWatchLogger.emitMetric({
      _aws: {
        Timestamp: Date.now(),
        CloudWatchMetrics: [
          {
            Namespace: "SafetyGenie/ClientCapture",
            Dimensions: [["captureMode", "objectType"]],
            Metrics: metricDefs,
          },
        ],
      },
      captureMode: params.captureMode,
      objectType: params.objectType,
      ...metricValues,
    });
  }

  /** Discard the current timing mark (e.g. step abandoned / unmounted). */
  cancel(): void {
    this.currentMark = null;
  }
}

export const captureTimingLogger = new CaptureTimingLogger();

export default captureTimingLogger;
